import React, { useState, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { Button } from '~/ui/button'
import { Input } from '~/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '~/ui/card'
import { Badge } from '~/ui/badge'
import {
  Search,
  Plus,
  Music,
  Clock,
  Users,
  Calendar,
  Volume2,
  VolumeX,
  Landmark,
} from 'lucide-react'
import { useDocumentTitle } from '~/common/use-document-title'
import { TrackModal } from './track-modal'
import { Skeleton } from '~/ui/skeleton'

const TRACKS_QUERY = gql(`
  query TracksQuery(
    $first: Int!
    $after: String
    $query: String
    $artistId: ID
    $albumId: ID
    $genreId: ID
    $year: Int
    $hasAudioUrl: Boolean
    $licensingPro: LicensingProEnum
    $sortBy: TrackSortByEnum
  ) {
    tracks(
      first: $first
      after: $after
      query: $query
      artistId: $artistId
      albumId: $albumId
      genreId: $genreId
      year: $year
      hasAudioUrl: $hasAudioUrl
      licensingPro: $licensingPro
      sortBy: $sortBy
    ) {
      edges {
        node {
          id
          name
          durationMs
          popularity
          explicit
          trackNumber
          discNumber
          displayArtist
          licensingPro
          licensingProCheckedAt
          playable
          createdAt
          coverThumbUrl
          audioUrl
          isSpotifyTrack
          album {
            id
            name
            releaseDate
          }
          artists {
            id
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }

    }
  }
`)

const ARTISTS_QUERY = gql(`
  query ArtistsForFilter {
    artists(first: 100) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`)

const ALBUMS_QUERY = gql(`
  query AlbumsForFilter {
    albums(first: 100) {
      edges {
        node {
          id
          name
          releaseDate
        }
      }
    }
  }
`)

const GENRES_QUERY = gql(`
  query GenresForFilter {
    genres(first: 100) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`)

interface TrackFilters {
  query: string
  artistId: string
  albumId: string
  genreId: string
  year: string
  hasAudioUrl: string
  licensingPro: string
  sortBy: string
}

const initialFilters: TrackFilters = {
  query: '',
  artistId: 'all',
  albumId: 'all',
  genreId: 'all',
  year: 'all',
  hasAudioUrl: 'all',
  licensingPro: 'all',
  sortBy: 'newest',
}

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'popularity', label: 'Most Popular' },
  { value: 'duration', label: 'Longest First' },
]

const AUDIO_FILTER_OPTIONS = [
  { value: 'all', label: 'All Tracks' },
  { value: 'true', label: 'With Audio' },
  { value: 'false', label: 'Without Audio' },
]

const LICENSING_PRO_OPTIONS = [
  { value: 'all', label: 'Any PRO' },
  { value: 'ASCAP', label: 'ASCAP' },
  { value: 'BMI', label: 'BMI' },
  { value: 'GMR', label: 'GMR' },
  { value: 'SESAC', label: 'SESAC' },
]

export function TracksScreen() {
  useDocumentTitle('Tracks')

  const [filters, setFilters] = useState<TrackFilters>(initialFilters)
  const [isTrackModalOpen, setIsTrackModalOpen] = useState(false)
  const [editingTrack, setEditingTrack] = useState<any>(null)

  const pageSize = 50

  // Query for tracks with filters
  const { data, loading, error, fetchMore, refetch } = useQuery(TRACKS_QUERY, {
    variables: {
      first: pageSize,
      query: filters.query || null,
      artistId: filters.artistId === 'all' ? null : filters.artistId,
      albumId: filters.albumId === 'all' ? null : filters.albumId,
      genreId: filters.genreId === 'all' ? null : filters.genreId,
      year: filters.year === 'all' ? null : parseInt(filters.year),
      hasAudioUrl: filters.hasAudioUrl === 'all' ? null : filters.hasAudioUrl === 'true',
      licensingPro: filters.licensingPro === 'all' ? null : (filters.licensingPro as any),
      sortBy: filters.sortBy as any,
    },
    notifyOnNetworkStatusChange: true,
  })

  // Queries for filter options
  const { data: artistsData } = useQuery(ARTISTS_QUERY)
  const { data: albumsData } = useQuery(ALBUMS_QUERY)
  const { data: genresData } = useQuery(GENRES_QUERY)

  const tracks = data?.tracks.edges.map((edge: any) => edge.node).filter(Boolean) || []
  const pageInfo = data?.tracks.pageInfo

  const handleFilterChange = useCallback((key: keyof TrackFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleClearFilters = useCallback(() => {
    setFilters(initialFilters)
  }, [])

  const handleLoadMore = useCallback(() => {
    if (pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
      })
    }
  }, [fetchMore, pageInfo])

  const handleEditTrack = useCallback((track: any) => {
    setEditingTrack(track)
    setIsTrackModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsTrackModalOpen(false)
    setEditingTrack(null)
  }, [])

  const handleTrackSaved = useCallback(() => {
    refetch()
    handleCloseModal()
  }, [refetch, handleCloseModal])

  const formatDuration = (durationMs: number) => {
    const minutes = Math.floor(durationMs / 60000)
    const seconds = Math.floor((durationMs % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getYearFromDate = (dateString: string) => {
    return new Date(dateString).getFullYear()
  }

  // Generate year options from albums
  const yearOptions = React.useMemo(() => {
    if (!albumsData?.albums.edges) return []

    const years = albumsData.albums.edges
      .map((edge: any) => edge.node?.releaseDate)
      .filter(Boolean)
      .map((date: any) => getYearFromDate(date!))
      .filter((year: any, index: any, array: any) => array.indexOf(year) === index)
      .sort((a: any, b: any) => b - a)

    return years.map((year: any) => ({ value: year.toString(), label: year.toString() }))
  }, [albumsData])

  const activeFiltersCount = Object.values(filters).filter(
    (value: any) => value && value !== 'newest' && value !== 'all'
  ).length

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Error loading tracks</p>
          <Button onClick={() => refetch()} variant="outline" className="mt-2">
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="sticky top-0 z-20 flex items-center justify-between border-b bg-white px-7 py-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tracks</h1>
          <p className="text-muted-foreground text-sm">Browse and manage your music tracks</p>
        </div>
        <Button onClick={() => setIsTrackModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Track
        </Button>
      </div>

      <div className="flex flex-1 flex-col">
        {/* Search and Filters */}
        <Card className="sticky top-[77px] z-20 gap-0 rounded-none border-0 border-b bg-neutral-50 pt-4 shadow-none">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Search &amp; Filters{' '}
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFiltersCount}
                  </Badge>
                )}
              </CardTitle>
              {activeFiltersCount > 0 && (
                <div className="flex justify-end">
                  <Button variant="outline" size="xs" onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4 !pt-2">
            {/* Search */}
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
              <Input
                placeholder="Search tracks, artists, or albums..."
                value={filters.query}
                onChange={(e) => handleFilterChange('query', e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <>
              {/*<Separator />*/}
              <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                {/* Artist Filter */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Artist</label>
                  <Select
                    value={filters.artistId}
                    onValueChange={(value) => handleFilterChange('artistId', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Any artist" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any artist</SelectItem>
                      {artistsData?.artists.edges.map(
                        (edge: any) =>
                          edge.node && (
                            <SelectItem key={edge.node.id} value={edge.node.id}>
                              {edge.node.name}
                            </SelectItem>
                          )
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Album Filter */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Album</label>
                  <Select
                    value={filters.albumId}
                    onValueChange={(value) => handleFilterChange('albumId', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Any album" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any album</SelectItem>
                      {albumsData?.albums.edges.map(
                        (edge: any) =>
                          edge.node && (
                            <SelectItem key={edge.node.id} value={edge.node.id}>
                              {edge.node.name}
                            </SelectItem>
                          )
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Genre Filter */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Genre</label>
                  <Select
                    value={filters.genreId}
                    onValueChange={(value) => handleFilterChange('genreId', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Any genre" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any genre</SelectItem>
                      {genresData?.genres.edges.map(
                        (edge: any) =>
                          edge.node && (
                            <SelectItem key={edge.node.id} value={edge.node.id}>
                              {edge.node.name}
                            </SelectItem>
                          )
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Year Filter */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Year</label>
                  <Select
                    value={filters.year}
                    onValueChange={(value) => handleFilterChange('year', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Any year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any year</SelectItem>
                      {yearOptions.map((option: any) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Licensing PRO Filter */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Licensing PRO</label>
                  <Select
                    value={filters.licensingPro}
                    onValueChange={(value) => handleFilterChange('licensingPro', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Any PRO" />
                    </SelectTrigger>
                    <SelectContent>
                      {LICENSING_PRO_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Audio Filter */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Audio</label>
                  <Select
                    value={filters.hasAudioUrl}
                    onValueChange={(value) => handleFilterChange('hasAudioUrl', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All tracks" />
                    </SelectTrigger>
                    <SelectContent>
                      {AUDIO_FILTER_OPTIONS.map((option: any) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Sort By */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Sort by</label>
                  <Select
                    value={filters.sortBy}
                    onValueChange={(value) => handleFilterChange('sortBy', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SORT_OPTIONS.map((option: any) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          </CardContent>
        </Card>

        {/* Tracks List */}
        <div className="space-y-4">
          {loading && tracks.length === 0 ? (
            // Loading skeletons
            <div className="space-y-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <Card key={i} className="rounded-none border-0 border-b shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-4">
                      <Skeleton className="h-12 w-12 rounded" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-[200px]" />
                        <Skeleton className="h-3 w-[150px]" />
                      </div>
                      <Skeleton className="h-4 w-[60px]" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : tracks.length === 0 ? (
            <Card className="rounded-none border-0 border-b shadow-none">
              <CardContent className="py-12 text-center">
                <Music className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No tracks found</h3>
                <p className="text-muted-foreground mb-4">
                  {activeFiltersCount > 0
                    ? 'Try adjusting your filters or search query'
                    : 'Get started by adding your first track'}
                </p>
                {activeFiltersCount > 0 ? (
                  <Button variant="outline" onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                ) : (
                  <Button onClick={() => setIsTrackModalOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Track
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="gap-0">
              {tracks.map(
                (track: any) =>
                  track && (
                    <Card
                      key={track.id}
                      className="hover:bg-muted/50 rounded-none border-0 border-b shadow-none transition-colors"
                    >
                      <CardContent className="px-4">
                        <div className="flex items-center space-x-4">
                          {/* Cover Art */}
                          <div className="relative h-12 w-12 flex-shrink-0">
                            {track.coverThumbUrl ? (
                              <img
                                src={track.coverThumbUrl}
                                alt={`${track.name} cover`}
                                className="h-12 w-12 rounded object-cover"
                              />
                            ) : (
                              <div className="bg-muted flex h-12 w-12 items-center justify-center rounded">
                                <Music className="text-muted-foreground h-6 w-6" />
                              </div>
                            )}
                            {track.playable ? (
                              <Volume2 className="bg-background absolute -right-1 -bottom-1 h-4 w-4 rounded-full p-0.5 text-green-600" />
                            ) : (
                              <VolumeX className="text-muted-foreground bg-background absolute -right-1 -bottom-1 h-4 w-4 rounded-full p-0.5" />
                            )}
                          </div>

                          {/* Track Info */}
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-center space-x-2">
                              <h3
                                className="hover:text-primary cursor-pointer truncate font-medium"
                                onClick={() => handleEditTrack(track)}
                              >
                                {track.name}
                              </h3>
                              {track.explicit && (
                                <Badge variant="secondary" className="text-xs">
                                  E
                                </Badge>
                              )}
                              {track.isSpotifyTrack && (
                                <Badge variant="outline" className="text-xs">
                                  Spotify
                                </Badge>
                              )}
                            </div>
                            <div className="text-muted-foreground flex flex-col items-start space-x-4 text-xs">
                              <div className="flex items-center">
                                <Users className="mr-1 h-3 w-3" />
                                {track.displayArtist}
                              </div>
                              {track.album && (
                                <div className="flex items-center">
                                  <Music className="mr-1 h-3 w-3" />
                                  {track.album.name}
                                </div>
                              )}
                              {track.album?.releaseDate && (
                                <div className="flex items-center">
                                  <Calendar className="mr-1 h-3 w-3" />
                                  {getYearFromDate(track.album.releaseDate)}
                                </div>
                              )}
                              <div className="flex items-center">
                                <Landmark className="mr-1 h-3 w-3" />
                                <Badge
                                  variant={track.licensingPro ? 'outline' : 'secondary'}
                                  className="text-[10px] tracking-wide uppercase"
                                >
                                  {track.licensingPro ?? 'Unmatched'}
                                </Badge>
                              </div>
                              {track.licensingProCheckedAt && (
                                <div className="text-[10px] text-neutral-500">
                                  Verified {formatDateTime(track.licensingProCheckedAt)}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Duration and Actions */}
                          <div className="text-muted-foreground flex items-center space-x-4 text-sm">
                            {track.durationMs && (
                              <div className="flex items-center">
                                <Clock className="mr-1 h-3 w-3" />
                                {formatDuration(track.durationMs)}
                              </div>
                            )}
                            <div className="text-xs">{formatDate(track.createdAt)}</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
              )}

              {/* Load More */}
              {pageInfo?.hasNextPage && (
                <div className="flex justify-center pt-4">
                  <Button variant="outline" onClick={handleLoadMore} disabled={loading}>
                    {loading ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Track Modal */}
        <TrackModal
          isOpen={isTrackModalOpen}
          onClose={handleCloseModal}
          onSaved={handleTrackSaved}
          track={editingTrack}
        />
      </div>
    </div>
  )
}
